import React from 'react';
import '../LandingPage.css';
import mbIcon from '../../assets/mb_icon.png';

interface SchoolAdmissionManagementProps {
  onBack: () => void;
  onNavigateToLogin: () => void;
  onNavigateToContact: () => void;
}

const SchoolAdmissionManagement: React.FC<SchoolAdmissionManagementProps> = ({ onBack, onNavigateToLogin, onNavigateToContact }) => {
  const features = [
    { icon: 'bi-file-earmark-text', title: 'Online Application Forms', desc: 'Customizable digital application forms with document uploads', color: '#5C2597' },
    { icon: 'bi-funnel', title: 'Lead Management', desc: 'Track inquiries from first contact to enrollment', color: '#2D7DD2' },
    { icon: 'bi-calendar-check', title: 'Interview Scheduling', desc: 'Automated scheduling with parent/student slot booking', color: '#28A745' },
    { icon: 'bi-file-check', title: 'Document Verification', desc: 'Digital document collection and verification workflow', color: '#FFC107' },
    { icon: 'bi-cash-coin', title: 'Fee Payment Integration', desc: 'Collect admission fees online with multiple payment options', color: '#E91E63' },
    { icon: 'bi-graph-up-arrow', title: 'Admission Analytics', desc: 'Track conversion rates, sources, and admission trends', color: '#00BCD4' },
  ];

  const workflow = [
    { icon: 'bi-globe', title: 'Online Inquiry', desc: 'Parents submit inquiry via website or WhatsApp' },
    { icon: 'bi-file-text', title: 'Application', desc: 'Fill online application with documents' },
    { icon: 'bi-person-check', title: 'Review', desc: 'Admin reviews and shortlists applications' },
    { icon: 'bi-calendar-event', title: 'Interview', desc: 'Schedule and conduct admission interview' },
    { icon: 'bi-check-circle', title: 'Selection', desc: 'Select students and send offer letters' },
    { icon: 'bi-mortarboard', title: 'Enrollment', desc: 'Complete enrollment and fee payment' },
  ];

  return (
    <div className="landing-page min-vh-100">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container-fluid px-4">
          <a className="navbar-brand d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <img src={mbIcon} alt="My Learning Board" className="mb-logo" />
            <span className="ms-2 fw-bold text-primary-custom">My Learning Board</span>
          </a>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary-custom" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Home
            </button>
            <button className="btn btn-primary-custom text-white" onClick={onNavigateToLogin}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #5C2597 0%, #9B59B6 100%)' }}>
        <div className="container-fluid px-4">
          <div className="row align-items-center py-5">
            <div className="col-lg-8 mx-auto text-center">
              <span className="badge bg-white text-primary mb-3 px-3 py-2">
                <i className="bi bi-mortarboard me-2"></i>Admission Management
              </span>
              <h1 className="display-4 fw-bold mb-4">
                School Admission Management Software
              </h1>
              <p className="lead mb-4 opacity-90">
                Digitize your entire admission process from inquiry to enrollment.
                Reduce paperwork by 90% and improve parent experience with online applications.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <button className="btn btn-light btn-lg px-4" onClick={onNavigateToContact}>
                  <i className="bi bi-calendar-event me-2"></i>Book Demo
                </button>
                <button className="btn btn-outline-light btn-lg px-4" onClick={onNavigateToLogin}>
                  <i className="bi bi-box-arrow-in-right me-2"></i>Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">End-to-End Admission Workflow</h2>
            <p className="text-muted lead">From first inquiry to successful enrollment</p>
          </div>
          <div className="row g-4">
            {workflow.map((step, idx) => (
              <div key={idx} className="col-6 col-md-4 col-lg-2">
                <div className="text-center">
                  <div className="position-relative mb-3">
                    <div className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center"
                      style={{ width: '70px', height: '70px' }}>
                      <i className={`bi ${step.icon} fs-4`}></i>
                    </div>
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-success">
                      {idx + 1}
                    </span>
                  </div>
                  <h6>{step.title}</h6>
                  <p className="small text-muted">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-5">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">Admission Management Features</h2>
          </div>
          <div className="row g-4">
            {features.map((feature, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="rounded d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '60px', height: '60px', backgroundColor: `${feature.color}15` }}>
                      <i className={`bi ${feature.icon} fs-4`} style={{ color: feature.color }}></i>
                    </div>
                    <h5 className="card-title">{feature.title}</h5>
                    <p className="card-text text-muted">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="fw-bold mb-4">Benefits of Digital Admissions</h2>
              <ul className="list-unstyled">
                {[
                  '90% reduction in paperwork and manual effort',
                  'Real-time tracking of application status',
                  'Automated communication with parents',
                  'Faster processing and decision making',
                  'Better data for admission planning',
                  'Improved parent satisfaction',
                ].map((benefit, idx) => (
                  <li key={idx} className="mb-3 d-flex align-items-center">
                    <i className="bi bi-check-circle-fill text-success me-3 fs-5"></i>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div className="row g-3">
                <div className="col-6">
                  <div className="bg-white rounded-4 p-4 shadow text-center">
                    <h2 className="fw-bold text-primary">90%</h2>
                    <p className="text-muted mb-0">Less Paperwork</p>
                  </div>
                </div>
                <div className="col-6">
                  <div className="bg-white rounded-4 p-4 shadow text-center">
                    <h2 className="fw-bold text-success">3x</h2>
                    <p className="text-muted mb-0">Faster Processing</p>
                  </div>
                </div>
                <div className="col-6">
                  <div className="bg-white rounded-4 p-4 shadow text-center">
                    <h2 className="fw-bold text-warning">100%</h2>
                    <p className="text-muted mb-0">Digital Records</p>
                  </div>
                </div>
                <div className="col-6">
                  <div className="bg-white rounded-4 p-4 shadow text-center">
                    <h2 className="fw-bold text-info">24/7</h2>
                    <p className="text-muted mb-0">Online Access</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4 text-center">
          <h2 className="mb-3">Modernize Your Admission Process</h2>
          <p className="lead opacity-75 mb-4">Join schools that have transformed their admissions with our software</p>
          <button className="btn btn-light btn-lg px-5" onClick={onNavigateToContact}>
            <i className="bi bi-rocket-takeoff me-2"></i>Get Started
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-4">
        <div className="container-fluid px-4 text-center">
          <p className="mb-0 small">&copy; 2026 Omnix Digital Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default SchoolAdmissionManagement;
